import { httpClient } from '@/services/httpClient';

/**
 * The shared client is fetch-based and ignores an axios-style `params` option, so the
 * query string is built here. Null, undefined and empty values are left out, so an unset filter is
 * "any" rather than the string "null"; a list repeats its key for each value.
 */
export const withQuery = (url, params = {}) => {
    const query = new URLSearchParams()

    const isBlank = value => value === null || value === undefined || value === ''

    // A list repeats its key once per value (`columns=a&columns=b`), as FastAPI reads a repeatable parameter.
    Object.entries(params).forEach(([key, value]) => {
        const values = Array.isArray(value) ? value : [value]
        values.filter(each => !isBlank(each)).forEach(each => query.append(key, String(each)))
    })

    const text = query.toString()

    return text ? `${url}?${text}` : url
}

// Evidence for the Statement Review page (insight-core line_item_review_router): how input
// lines became fees, and whether their volume behaves like transaction volume. Read-only.

const getSplitSummary = async (params = {}) => {
    const response = await httpClient.get(withQuery('/api/v1/line-item-review/summary', params))

    return response.data
}

const getReviewLines = async (params = {}) => {
    const response = await httpClient.get(withQuery('/api/v1/line-item-review/lines', params))

    return response.data
}

/** Every line the filters match, as CSV text - one row per fee. */
const exportReviewLines = async (params = {}) => {
    const response = await httpClient.get(withQuery('/api/v1/line-item-review/lines/export', params))

    return response.data
}

const getProviderHealth = async () => {
    const response = await httpClient.get('/api/v1/line-item-review/health')

    return response.data
}

const getClassificationDepth = async () => {
    const response = await httpClient.get('/api/v1/line-item-review/classification-depth')

    return response.data
}

// Stated interchange and scheme charges against published rates.

const getAuditSummary = async () => {
    const response = await httpClient.get('/api/v1/line-item-review/audit')

    return response.data
}

const getAuditRows = async (params = {}) => {
    const response = await httpClient.get(withQuery('/api/v1/line-item-review/audit/rows', params))

    return response.data
}

// The value queue and its answers (insight-core volume_role_router). An answer is shared
// (every tenant), this tenant's, or one line's - see docs/plans/statement-review-redesign.md.

/** Every specification's row; `status` (todo, done, reviewed, differs) narrows it. */
const getVolumeRoleQueue = async (status = null) => {
    const response = await httpClient.get(withQuery('/api/v1/volume-roles', { status }))

    return response.data
}

/** Shared answers. Each result says how many other tenants it also applies to. */
const saveSharedVolumeRoles = async (answers) => {
    const response = await httpClient.put('/api/v1/volume-roles/shared', answers)

    return response.data
}

/** This tenant has seen these shared answers as they stand. */
const acknowledgeVolumeRoles = async (answerIds) => {
    const response = await httpClient.post('/api/v1/volume-roles/acknowledge', { answer_ids: answerIds })

    return response.data
}

const getVolumeRoleLines = async ({ provider, standardised_specification_id }) => {
    const response = await httpClient.get(withQuery('/api/v1/volume-roles/lines', {
        provider, standardised_specification_id,
    }))

    return response.data
}

/** This tenant's answers, or single lines'. One equal to the shared answer is dropped. */
const saveVolumeRoles = async (tags) => {
    const response = await httpClient.put('/api/v1/volume-roles', tags)

    return response.data
}

/** "Use the shared answer": withdraws this tenant's or a line's answer. */
const deleteVolumeRole = async (tagId) => {
    await httpClient.delete(`/api/v1/volume-roles/${tagId}`)
}

const getLayoutVerifications = async () => {
    const response = await httpClient.get('/api/v1/volume-roles/layouts')

    return response.data
}

const setLayoutVerification = async (payload) => {
    const response = await httpClient.put('/api/v1/volume-roles/layouts', payload)

    return response.data
}

export {
    getSplitSummary,
    getReviewLines,
    exportReviewLines,
    getProviderHealth,
    getClassificationDepth,
    getAuditSummary,
    getAuditRows,
    getVolumeRoleQueue,
    getVolumeRoleLines,
    saveSharedVolumeRoles,
    acknowledgeVolumeRoles,
    saveVolumeRoles,
    deleteVolumeRole,
    getLayoutVerifications,
    setLayoutVerification,
}
